/* eslint-disable no-restricted-syntax */

import {getInclusionDirectives} from '@apollo/client/utilities';

import type {
  StringValueNode,
  ObjectFieldNode,
  ArgumentNode,
  ObjectValueNode,
  DirectiveNode,
  FieldNode,
  OperationDefinitionNode,
  VariableDefinitionNode,
} from 'graphql';

import type {
  FragmentPathMap,
  ConditionalFragmentMap,
  DecisionTree,
  ConditionalNode,
  VisitorStack,
  InlinePredicate,
} from './types';

import {ROOT_PATH} from './enums';

export type NodeLookupMap = {
  [key: string]: Array<string>;
};

const getStringObjectField = (
  name: string,
  value: string,
): ObjectFieldNode => ({
  kind: 'ObjectField',
  name: {
    kind: 'Name',
    value: name,
  },
  value: {
    kind: 'StringValue',
    value,
  },
});

export const formatStringValueNodes = (
  values: Array<string>,
): Array<StringValueNode> =>
  values.map((value) => ({
    kind: 'StringValue',
    value,
  }));

const getFragmentListValues = (
  fragments: FragmentPathMap,
): Array<ObjectValueNode> => {
  const values: Array<ObjectValueNode> = [];

  for (const [fragmentName, paths] of fragments.entries()) {
    values.push({
      kind: 'ObjectValue',
      fields: [
        {
          kind: 'ObjectField',
          name: {
            kind: 'Name',
            value: 'name',
          },
          value: {
            kind: 'StringValue',
            value: fragmentName,
          },
        },
        {
          kind: 'ObjectField',
          name: {
            kind: 'Name',
            value: 'paths',
          },
          value: {
            kind: 'ListValue',
            values: formatStringValueNodes(paths),
          },
        },
      ],
    });
  }

  return values;
};

export const getFragmentParams = (fragments: FragmentPathMap): ArgumentNode => {
  const values = getFragmentListValues(fragments);

  return {
    kind: 'Argument',
    name: {
      kind: 'Name',
      value: 'fragments',
    },
    value: {
      kind: 'ListValue',
      values,
    },
  };
};

const getPathParams = (
  type: string,
  pathParams: Array<string>,
): ArgumentNode => {
  const values = formatStringValueNodes(pathParams);

  return {
    kind: 'Argument',
    name: {
      kind: 'Name',
      value: type,
    },
    value: {
      kind: 'ListValue',
      values,
    },
  };
};

export const getIncludeOnlyParams = (
  includeOnly: Array<string>,
): ArgumentNode => getPathParams('includeOnly', includeOnly);

export const getExpandParams = (expands: Array<string>): ArgumentNode =>
  getPathParams('expand', expands);

export const getNodeLookupMapParams = (
  nodeLookupMap: NodeLookupMap,
): ArgumentNode => {
  const values: Array<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | any
    | {
        readonly fields: ReadonlyArray<ObjectFieldNode>;
        readonly kind: 'ObjectValue';
        readonly loc?: Location;
      }
  > = [];

  for (const path of Object.keys(nodeLookupMap)) {
    values.push({
      kind: 'ObjectValue',
      fields: [
        {
          kind: 'ObjectField',
          name: {
            kind: 'Name',
            value: 'nodes',
          },
          value: {
            kind: 'ListValue',
            values: formatStringValueNodes(nodeLookupMap[path]),
          },
        },
        getStringObjectField('parentPath', path),
      ],
    });
  }

  return {
    kind: 'Argument',
    name: {
      kind: 'Name',
      value: 'nodeLookupMap',
    },
    value: {
      kind: 'ListValue',
      values,
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getListObjectField = (name: string, values: Array<any>) => ({
  kind: 'ObjectField',
  name: {
    kind: 'Name',
    value: name,
  },
  value: {
    kind: 'ListValue',
    values,
  },
});

const getConditionalNodeParams = (node: ConditionalNode): ObjectValueNode => {
  const baseFields = [
    getStringObjectField('type', node.type),
    getStringObjectField('path', node.path),
    getStringObjectField('variable', node.variable),
    getListObjectField('includeOnly', formatStringValueNodes(node.includeOnly)),
    getStringObjectField('expand', node.expand),
    getListObjectField('fragments', getFragmentListValues(node.fragments)),
  ];

  if (node.children.length === 0) {
    return {
      kind: 'ObjectValue',
      // @ts-expect-error - TS2322 - Type 'ObjectFieldNode | { kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'. | TS2322 - Type '{ kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'.
      // eslint-disable-next-line @stripe-internal/stripe/no-props-suppress-errors
      fields: [...baseFields, getListObjectField('children', [])],
    };
  }

  return {
    kind: 'ObjectValue',
    fields: [
      // @ts-expect-error - TS2322 - Type 'ObjectFieldNode | { kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'.
      ...baseFields,
      // @ts-expect-error - TS2322 - Type '{ kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'.
      getListObjectField(
        'children',
        node.children.map(getConditionalNodeParams),
      ),
    ],
  };
};

export const getPredicatesParams = (predicates: DecisionTree): ArgumentNode => {
  if (predicates.ROOT.children.length === 0) {
    return {
      kind: 'Argument',
      name: {
        kind: 'Name',
        value: 'predicates',
      },
      value: {
        kind: 'ObjectValue',
        fields: [],
      },
    };
  }

  return {
    kind: 'Argument',
    name: {
      kind: 'Name',
      value: 'predicates',
    },
    value: {
      kind: 'ObjectValue',
      fields: [
        {
          kind: 'ObjectField',
          name: {
            kind: 'Name',
            value: 'ROOT',
          },
          value: {
            kind: 'ObjectValue',
            fields: [
              // @ts-expect-error - TS2322 - Type '{ kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'.
              getListObjectField(
                'children',
                predicates.ROOT.children.map(getConditionalNodeParams),
              ),
              // @ts-expect-error - TS2322 - Type '{ kind: string; name: { kind: string; value: string; }; value: { kind: string; values: any[]; }; }' is not assignable to type 'ObjectFieldNode'.
              getListObjectField(
                'variables',
                formatStringValueNodes(predicates.ROOT.variables),
              ),
            ],
          },
        },
      ],
    },
  };
};

type PathInfo = {
  parentPath: string;
  nodeName: string;
};

export const getPathInfo = (path: string): PathInfo => {
  const segments = path.split('.');
  let parentPath = segments.slice(0, -1).join('.');
  if (parentPath === '') {
    parentPath = ROOT_PATH;
  }
  return {
    parentPath,
    nodeName: segments[segments.length - 1],
  };
};

export const bucketNodesByPath = (includes: Array<string>): NodeLookupMap => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nodeLookup: Record<string, any> = {};
  includes.forEach((include) => {
    const {parentPath, nodeName} = getPathInfo(include);
    const childNodes = nodeLookup[parentPath];
    if (!childNodes) {
      nodeLookup[parentPath] = [];
    }
    // bucket fields by their parents
    if (!nodeLookup[parentPath].includes(nodeName)) {
      nodeLookup[parentPath].push(nodeName);
    }
  });
  return nodeLookup;
};

export const appendDecisionTreeNode = (
  tree: DecisionTree,
  targetNode: ConditionalNode,
) => {
  let current = tree.ROOT;
  const {path: targetpath} = targetNode;
  if (!tree.ROOT.variables.includes(targetNode.variable)) {
    tree.ROOT.variables.push(targetNode.variable);
  }

  let notFound = true;
  while (notFound) {
    const parentNode = current.children.find(
      // synthetic node should not have any children
      (node) => targetpath.startsWith(node.path) && !node.synthetic,
    );
    if (parentNode) {
      // @ts-expect-error - TS2741 - Property 'variables' is missing in type 'ConditionalNode' but required in type '{ variables: string[]; children: ConditionalNode[]; }'.
      current = parentNode;
    } else {
      current.children.push(targetNode);
      notFound = false;
    }
  }
};

const findParentNode = (
  node: ConditionalNode,
  targetPath: string,
): ConditionalNode | null => {
  if (node.path === targetPath) {
    return node;
  }
  const current = node.children.find(({path}) => targetPath.startsWith(path));
  if (!current) {
    if (targetPath.startsWith(node.path)) {
      return node;
    }
    return null;
  }

  return findParentNode(current, targetPath);
};

const insertIncludeOnlyToDecisionTree = (
  tree: DecisionTree,
  includeOnly: Array<string>,
) => {
  includeOnly.forEach((include) => {
    for (const topLevelNode of tree.ROOT.children) {
      const node = findParentNode(topLevelNode, include);
      if (node) {
        // if the target path belongs to the current top-level field
        node.includeOnly.push(include);
        break;
      }
    }
  });
};

const markPath = (node: ConditionalNode, targetPath: string) => {
  const isSubPath = targetPath.startsWith(node.path);
  if (isSubPath) {
    // if the current path is a subpath of the fully expanded path
    // append the current path as the partially expanded path first
    node.expand = node.path;
  }
  // In the case of synthetic nodes, we might have multiple sibling nodes with the same path
  const current = node.children.filter(({path}) => targetPath.startsWith(path));
  if (!current.length) {
    if (isSubPath) {
      // append the fully expanded path if the current path is the last conditional directive
      // on the expand path
      node.expand = targetPath;
    }
    return;
  }

  current.map((eachMatch) => markPath(eachMatch, targetPath));
};

const insertExpandsToDecisionTree = (
  tree: DecisionTree,
  expands: Array<string>,
) => {
  expands.forEach((expand) => {
    tree.ROOT.children.forEach((topLevelNode) =>
      markPath(topLevelNode, expand),
    );
  });
};

type FragmentNode = {
  readonly fragmentName: string;
  readonly parentPath: string;
};

/**
 * When a FragmentSpread node is directly annotated by @skip or @include we
 * need to perform further evaluation on the inline FragmentSpread predicate in
 * addition to the ancestor predicates.
 *
 * The solution proposed here is simply creating a "Synthetic" decision tree
 * node where the predicate information is carried over and only contains the
 * conditional fragment info.
 */
const getSyntheticNode = (
  {parentPath, fragmentName}: FragmentNode,
  inlinePredicate: InlinePredicate,
): ConditionalNode => {
  const fragments: FragmentPathMap = new Map([[fragmentName, [parentPath]]]);

  return {
    type: inlinePredicate.type,
    variable: inlinePredicate.variable,
    path: parentPath,
    includeOnly: [],
    expand: '', // This value will get overwritten by the `insertExpandsToDecisionTree` call.
    fragments,
    children: [],
    synthetic: true,
  };
};

const insertFragmentNodeToDecisionTree = (
  tree: DecisionTree,
  {fragmentName, parentPath}: FragmentNode,
) => {
  for (const topLevelNode of tree.ROOT.children) {
    const node = findParentNode(topLevelNode, parentPath);

    if (node) {
      const existingPaths = node.fragments.get(fragmentName) || [];

      if (!existingPaths.includes(parentPath)) {
        node.fragments.set(fragmentName, [...existingPaths, parentPath]);
      }

      break;
    }
  }
};

const insertFragmentsToDecisionTree = (
  tree: DecisionTree,
  fragments: ConditionalFragmentMap,
) => {
  for (const [fragmentName, paths] of fragments.entries()) {
    paths.forEach(({parentPath, inlinePredicate}) => {
      const fragmentNode = {fragmentName, parentPath} as const;
      if (inlinePredicate) {
        const syntheticNode = getSyntheticNode(fragmentNode, inlinePredicate);
        appendDecisionTreeNode(tree, syntheticNode);
      } else {
        insertFragmentNodeToDecisionTree(tree, fragmentNode);
      }
    });
  }
};

// TODO: Consolidate all decision tree related methods into its own class.
export const updateDecisionTree = (
  tree: DecisionTree,
  includeOnly: Array<string>,
  expands: Array<string>,
  fragments: ConditionalFragmentMap,
) => {
  insertIncludeOnlyToDecisionTree(tree, includeOnly);
  insertFragmentsToDecisionTree(tree, fragments);
  insertExpandsToDecisionTree(tree, expands);
};

export const hasConditionalDirectives = (stack: VisitorStack): boolean => {
  const nodes = stack.map(({node}) => node).filter(Boolean);

  const directives = getInclusionDirectives(
    nodes.flatMap((node) => node?.directives || []),
  );

  if (directives.length) {
    return true;
  }

  return false;
};

export const getInlinePredicate = (
  directives: ReadonlyArray<DirectiveNode>,
  nodePath: string,
): InlinePredicate | null | undefined => {
  const inclusionDirectives = getInclusionDirectives(directives);
  if (inclusionDirectives.length) {
    if (inclusionDirectives.length > 1) {
      throw new Error(
        `Please only specific a single inclusion directive for ${nodePath}`,
      );
    }
    const {directive, ifArgument} = inclusionDirectives[0];
    // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'ValueNode'.
    const inputVariable = ifArgument.value.name.value;

    return {
      type: directive.name.value as 'include' | 'skip',
      variable: inputVariable,
    };
  }
};

// Get v1Context variable definition from operation node.
const getOverrides = (
  operationNode: OperationDefinitionNode,
): VariableDefinitionNode | undefined => {
  return operationNode.variableDefinitions?.find(
    (v) =>
      v.type.kind === 'NonNullType' &&
      v.type.type.kind === 'NamedType' &&
      v.type.type.name.value === 'V1ContextInput',
  );
};

// If v1Context are already present as argument to operation, do nothing
// else, add them.
export const ensureOverridesToOperation = (
  operationNode: OperationDefinitionNode,
): OperationDefinitionNode => {
  // Check if v1Context are already present.
  if (getOverrides(operationNode)) {
    return operationNode;
  }

  // Add v1Context to variable definitions.
  const v1ContextDefinitionNode: VariableDefinitionNode = {
    kind: 'VariableDefinition',
    variable: {
      kind: 'Variable',
      name: {
        kind: 'Name',
        value: 'v1Context',
      },
    },
    type: {
      kind: 'NonNullType',
      type: {
        kind: 'NamedType',
        name: {
          kind: 'Name',
          value: 'V1ContextInput',
        },
      },
    },
  };

  const variableDefinitions = [
    ...(operationNode.variableDefinitions || []),
    v1ContextDefinitionNode,
  ];

  return {
    ...operationNode,
    variableDefinitions,
  };
};

// If v1Context are already present as argument to top-level field, do nothing
// else, add them.
export const ensureOverridesToField = (
  node: FieldNode,
  operationNode: OperationDefinitionNode,
): FieldNode => {
  const v1Context = getOverrides(operationNode);

  // Should not happen, because ensureOverridesToOperation should have already
  // been called.
  if (!v1Context) {
    throw new Error(
      `Overrides missing from graphql operation: ${operationNode}`,
    );
  }

  // Check if v1Context is already present on the field.
  if (
    node.arguments?.some(
      (arg) =>
        arg.value.kind === 'Variable' &&
        arg.value.name.value === v1Context.variable.name.value,
    )
  ) {
    return node;
  }

  const v1ContextArgumentNode: ArgumentNode = {
    kind: 'Argument',
    name: v1Context.variable.name,
    value: v1Context.variable,
  };

  const args = node.arguments
    ? [...node.arguments, v1ContextArgumentNode]
    : [v1ContextArgumentNode];

  return {...node, arguments: args};
};
